// PostHog user identification helpers.
// PostHog is initialised once in src/index.tsx and exposes a singleton via the
// `posthog-js` default import, so these helpers can call it directly without the
// React hook (handy for boot-time / non-component code paths).
//
// These mirror the Clarity helpers (see src/helpers/clarity.ts): they identify
// the logged-in user by email so events/sessions can be tracked and filtered
// per user in the PostHog dashboard.

import posthog from "posthog-js";
import { friendlyNameFromEmail } from "./clarity";

/**
 * Identify the current PostHog user by email.
 * - Uses the raw email as the stable distinct_id (matches Clarity's custom-id).
 * - Sets person properties (email, name, org) so you can filter/segment in the UI.
 */
export const identifyPostHogUser = (email?: string | null) => {
  if (!email || typeof window === "undefined" || !posthog?.__loaded) {
    return;
  }
  try {
    const [, domainPart = ""] = email.split("@");
    const org = domainPart.split(".")[0]?.toUpperCase() ?? "";
    posthog.identify(email, {
      email,
      name: friendlyNameFromEmail(email),
      org,
    });
  } catch (e) {
    // Analytics must never break the app.
    console.warn("PostHog identify failed", e);
  }
};

/**
 * Re-identify an already-logged-in user from localStorage (e.g. on app boot or
 * route change, where the session is restored without going through login).
 */
export const identifyPostHogFromStorage = () => {
  try {
    if (localStorage.getItem("loggedIn") === "true") {
      identifyPostHogUser(localStorage.getItem("email"));
    }
  } catch {
    // ignore storage access errors
  }
};

/**
 * Map a pathname to the tenant/dashboard family it belongs to. This app is
 * multi-tenant (ruvnl, gridco, CSPDCL, hppc, jvvnl, demo, dashboard), so a flat
 * URL list in PostHog is hard to read — tagging every pageview/event with the
 * tenant lets you break down "Pages" and any insight by tenant.
 */
export const tenantFromPath = (pathname?: string | null): string => {
  const path = (pathname ?? "").toLowerCase();
  if (path.startsWith("/ruvnl")) return "ruvnl";
  if (path.startsWith("/jvvnl")) return "jvvnl";
  if (path.startsWith("/cspdcl")) return "cspdcl";
  if (path.startsWith("/gridco")) return "gridco";
  if (path.startsWith("/hppc")) return "hppc";
  if (path.startsWith("/demo")) return "demo";
  if (path.startsWith("/dashboard")) return "dashboard";
  if (path.startsWith("/login")) return "login";
  return "other";
};

/**
 * Capture a SPA pageview with tenant context, and register the tenant as a
 * super property so EVERY subsequent event (clicks, filter changes, custom
 * events) is automatically attributed to the right tenant. Call on each route
 * change. This is what makes the per-page/per-tenant graphs populate usefully.
 */
export const capturePageview = (pathname?: string | null) => {
  try {
    if (typeof window === "undefined" || !posthog?.__loaded) return;
    const path = pathname ?? window.location.pathname;
    const tenant = tenantFromPath(path);
    posthog.register({ tenant });
    posthog.capture("$pageview", { tenant, pathname: path });
  } catch (e) {
    console.warn("PostHog pageview failed", e);
  }
};

/**
 * Capture a custom product event. Once a user is identified (on login / from
 * storage), every event is automatically attributed to that user, so you get
 * user-wise event tracking out of the box.
 *
 * Usage: captureEvent("bid_created", { class: "thermal", mw: 120 });
 */
export const captureEvent = (
  event: string,
  properties?: Record<string, any>,
) => {
  try {
    if (typeof window !== "undefined" && posthog?.__loaded) {
      posthog.capture(event, properties);
    }
  } catch (e) {
    console.warn("PostHog capture failed", e);
  }
};

/**
 * Reset PostHog on logout so subsequent (anonymous) events are not attributed
 * to the previous user. Call this wherever the session is torn down.
 */
export const resetPostHog = () => {
  try {
    if (typeof window !== "undefined" && posthog?.__loaded) {
      posthog.reset();
    }
  } catch (e) {
    console.warn("PostHog reset failed", e);
  }
};
